import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Clock, Play, CheckCircle, AlertTriangle, Wrench, Calendar } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";

export function OperatorDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Mis órdenes asignadas
  const { data: myOrders, isLoading: ordersLoading } = useQuery({
    queryKey: ["my-orders", user?.id],
    queryFn: async () => {
      const { data, error } = await api
        .from("work_orders")
        .select("id, work_order_number, product_name, status, priority, created_at")
        .eq("assigned_to", user?.id)
        .in("status", ["pending", "in_progress"])
        .order("priority", { ascending: false })
        .limit(5);
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  // Mis tareas pendientes
  const { data: myTasks, isLoading: tasksLoading } = useQuery({
    queryKey: ["my-tasks", user?.id],
    queryFn: async () => {
      const { data, error } = await api
        .from("tasks")
        .select("id, title, status, priority, due_date")
        .eq("assigned_to", user?.id)
        .in("status", ["pending", "in_progress"])
        .order("due_date", { ascending: true })
        .limit(5);
      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });

  // Tiempo trabajado hoy
  const { data: todayTime } = useQuery({
    queryKey: ["today-time", user?.id],
    queryFn: async () => {
      const today = new Date().toISOString().split("T")[0];
      const { data, error } = await api
        .from("work_sessions")
        .select("duration_minutes")
        .eq("user_id", user?.id)
        .gte("start_time", today);
      if (error) return 0;
      return data?.reduce((acc, s) => acc + (s.duration_minutes || 0), 0) || 0;
    },
    enabled: !!user?.id,
  });

  const formatTime = (minutes: number) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h ${m}m`;
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "urgent": return "bg-red-500";
      case "high": return "bg-orange-500";
      case "normal": return "bg-blue-500";
      default: return "bg-gray-500";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "in_progress": return <Play className="w-4 h-4 text-green-500" />;
      case "pending": return <Clock className="w-4 h-4 text-yellow-500" />;
      default: return <CheckCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header personal */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Hola, {user?.user_metadata?.full_name?.split(" ")[0] || "Operario"}</h2>
          <p className="text-muted-foreground">Tu resumen de trabajo de hoy</p>
        </div>
        <div className="flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-lg">
          <Clock className="w-5 h-5 text-primary" />
          <div>
            <p className="text-xs text-muted-foreground">Tiempo hoy</p>
            <p className="text-lg font-bold text-primary">{formatTime(todayTime || 0)}</p>
          </div>
        </div>
      </div>

      {/* Métricas rápidas */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                <Wrench className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{myOrders?.length || 0}</p>
                <p className="text-xs text-muted-foreground">Órdenes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{myTasks?.length || 0}</p>
                <p className="text-xs text-muted-foreground">Tareas</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 dark:bg-yellow-900 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {myOrders?.filter(o => o.priority === "urgent" || o.priority === "high").length || 0}
                </p>
                <p className="text-xs text-muted-foreground">Urgentes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                <Calendar className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {myTasks?.filter(t => t.due_date && new Date(t.due_date) <= new Date()).length || 0}
                </p>
                <p className="text-xs text-muted-foreground">Vencidas</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Mis órdenes */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-lg">Mis Órdenes de Trabajo</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate("/work-orders")}>
            Ver todas
          </Button>
        </CardHeader>
        <CardContent>
          {ordersLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-16" />)}
            </div>
          ) : myOrders?.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No tienes órdenes asignadas
            </p>
          ) : (
            <div className="space-y-3">
              {myOrders?.map(order => (
                <div
                  key={order.id}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/work-orders/${order.id}`)}
                >
                  <div className="flex items-center gap-3">
                    {getStatusIcon(order.status)}
                    <div>
                      <p className="font-medium">{order.work_order_number}</p>
                      <p className="text-sm text-muted-foreground truncate max-w-[200px]">
                        {order.product_name}
                      </p>
                    </div>
                  </div>
                  <Badge className={getPriorityColor(order.priority)}>
                    {order.priority}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mis tareas */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-lg">Mis Tareas</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate("/tasks")}>
            Ver todas
          </Button>
        </CardHeader>
        <CardContent>
          {tasksLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-14" />)}
            </div>
          ) : myTasks?.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No tienes tareas pendientes
            </p>
          ) : (
            <div className="space-y-3">
              {myTasks?.map(task => (
                <div
                  key={task.id}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/tasks`)}
                >
                  <div className="flex items-center gap-3">
                    {getStatusIcon(task.status)}
                    <p className="font-medium truncate max-w-[250px]">{task.title}</p>
                  </div>
                  {task.due_date && (
                    <span className={`text-xs ${new Date(task.due_date) < new Date() ? "text-red-500" : "text-muted-foreground"}`}>
                      {formatDistanceToNow(new Date(task.due_date), { addSuffix: true, locale: es })}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
